import React, { useEffect, useLayoutEffect, useState } from 'react';
import { createTheme, ThemeProvider, styled } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';
import AppBar from '@mui/material/AppBar';
import Drawer from '@mui/material/Drawer';
import Toolbar from '@mui/material/Toolbar';
import Hidden from '@mui/material/Hidden';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Container from '@mui/material/Container';
import MenuIcon from '@mui/icons-material/Menu';
import LinkPDF from '@mui/material/Link';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import DepartmentListItems from '@/components/appShell/DepartmentListItems';
import AdminListItems from '@/components/appShell/AdminListItems';
import AdminAnnounces from '@/components/appShell/AdminAnnounces';
import MenuItem from '@mui/material/MenuItem';
import Menu from '@mui/material/Menu';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/common/hooks/useAuth';
import WelcomeName from '@/components/appShell/WelcomeName';
import Tooltip from '@mui/material/Tooltip';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import MoreIcon from '@mui/icons-material/MoreVert';
import LogoutIcon from '@mui/icons-material/Logout';
import Avatar from '@mui/material/Avatar';
import ListItemIcon from '@mui/material/ListItemIcon';
import MyGroupListItems from '@/components/appShell/MyGroupListItems';
import { Role } from '@/common/constants';
import Copyright from '@/components/common/Copyright';
import FeedbackIcon from '@mui/icons-material/Feedback';
import FindInPageIcon from '@mui/icons-material/FindInPage';
import { ChildDepartments, Department } from '@/types/Department.type';
import DepartmentService from '@/services/Department.service';
import MyGroupService from '@/services/MyGroup.service';
import { MyGroup } from '@/types/MyGroup.type';
import URL from '@/common/routes/Url';
import useSidebar from '@/common/hooks/useSidebar';
import HistoryService from '@/services/History.service';
import { History } from '@/common/constants/History';
import HistoryManagement from '@/components/appShell/HistoryManagement';

import CompanyType from '@/types/Company.type';
import CompanyService from '@/services/Company.service';

const drawerWidth = 245;

const CustomAppBar = styled(AppBar)(({ theme }) => ({
  zIndex: theme.zIndex.drawer + 1,
  root: {
    [theme.breakpoints.up('lg')]: {
      width: `calc(100% - ${drawerWidth}px)`,
      marginLeft: drawerWidth,
    },
  },
}));

const CustomDrawer = styled(Drawer)(({ theme }) => ({
  paper: {
    width: drawerWidth,
    height: '100dvh',
    [theme.breakpoints.up('sm')]: {
      position: 'relative',
    },
  },
}));

const CustomIconButton = styled(IconButton)(({ theme }) => ({
  marginRight: theme.spacing(2),
  [theme.breakpoints.up('lg')]: {
    display: 'none',
  },
}));

const mdTheme = createTheme({
  palette: {
    primary: {
      main: "#0071bc"
    },
  }
});

function pageName(location: string) {
  let name: string = "";

  switch (location) {
    case "/employees":
      name = "社員マスタ";
      break;
    case "/employees/add":
      name = "社員マスタ ー 新規追加";
      break;
    case location.startsWith("/employees/") && location :
      name = "社員マスタ ー 編集";
      break;
    case "/departments":
      name = "部署マスタ";
      break;
    case "/departments/add":
      name = "部署マスタ ー 新規追加";
      break;
    case location.startsWith("/departments/") && location :
      name = "部署マスタ ー 編集";
      break;
    case "/phones":
      name = "共有電話マスタ";
      break;
    case "/phones/add":
      name = "共有電話マスタ ー 新規追加";
      break;
    case location.startsWith("/phones/") && location :
      name = "共有電話マスタ ー 編集";
      break;
    case "/styles":
      name = "勤務形態マスタ";
      break;
    case "/styles/add":
      name = "勤務形態マスタ ー 新規追加";
      break;
    case location.startsWith("/styles/") && location :
      name = "勤務形態マスタ ー 編集";
      break;
    case "/statuses":
      name = "ステータスマスタ";
      break;
    case "/statuses/add":
      name = "ステータスマスタ ー 新規追加";
      break;
    case location.startsWith("/statuses/") && location :
      name = "ステータスマスタ ー 編集";
      break;
    case "/company":
      name = "会社マスタ";
      break;
    case "/announces":
      name = "アナウンス";
      break;
    case "/announces/add":
      name = "アナウンス ー 新規追加";
      break;
    case location.startsWith("/announces/") && location :
      name = "アナウンス ー 編集";
      break;
    case "/history-export":
      name = "操作履歴";
      break;
    case "/my-groups":
      name = "Myグループ一覧";
      break;
    case "/my-groups/add":
      name = "Myグループ ー 新規追加";
      break;
    case location.startsWith("/my-groups/") && location :
      name = "Myグループ ー 編集";
      break;
    case location.startsWith("/employee-my-groups/") && location :
        name = "Myグループ ー メンバー編集";
        break;
    case location.startsWith("/plan/") && location :
      name = "予定登録";
      break;
    case location.startsWith("/profile") && location :
      name = "個人設定";
      break;
    default:
      break;
  }

  return name;
};

interface Props {
  children?: React.ReactNode;
}

const GenericTemplate: React.FC<Props> = ({ children }) => {
  const { sidebarReload, setSidebarReload } = useSidebar();
  // ポップアップ周り
  const [open, setOpen] = useState(false);
  const toggleDrawer = () => {
    setOpen(!open);
  };
  const [MoreAnchorEl, setMoreAnchorEl] = useState<null | HTMLElement>(null);
  const navigation = useNavigate();

  const personal = () => {
    navigation(URL.profile);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMoreAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMoreAnchorEl(null);
  };

  // 権限制御
  const auth = useAuth();
  const [showAdminBoard, setShowAdminBoard] = useState<boolean>(false);
  const [showAnnounceBoard, setShowAnnounceBoard] = useState<boolean>(false);
  const [showHistoryManagement, setHistoryManagement] = useState<boolean>(false);

  const [departments, setDepartments] = useState<Array<ChildDepartments & Department>>([]);
  const [myGroup, setMyGroup] = useState<Array<MyGroup>>([]);
  // 会社マスタ 契約プラン
  const [contPlan, setContPlan] = useState<Number>(0);

  useLayoutEffect(() => {
    if (auth.user) {
      if (auth.user.role === Role.管理者) {
        setShowAdminBoard(true);
      }else{
        setShowAdminBoard(false);
      }

      if (auth.user.role === Role.管理者 || auth.user.role === Role.アナウンス) {
        if ( contPlan === 1 ) {
          setShowAnnounceBoard(true);
        }else{
          setShowAnnounceBoard(false);
        }
      }
    }
  }, [auth.user.role,contPlan]);

  useLayoutEffect(() => {
    DepartmentService.getAll({ params: { parent: 1 } }).then(
      (response: { data: Array<ChildDepartments & Department> }) => {
        setDepartments(response.data);
      },
    );
    MyGroupService.getAvailables().then((response: { data: MyGroup[] }) => {
      setMyGroup(response.data);
    });

    // 会社マスタ取得
    CompanyService.get().then((response: { data: CompanyType }) => {
      let company : any = response.data;
      setContPlan(company['contPlan']);   //cont_plan 1:有効 0:無効
      setHistoryManagement(company['contPlan'] == 1);   //cont_plan 1:有効 0:無効
    });

  }, []);

  useEffect(() => {
    if (!sidebarReload) {
      return;
    }
    DepartmentService.getAll({ params: { parent: 1 } }).then(
      (response: { data: Array<ChildDepartments & Department> }) => {
        setDepartments(response.data);
      },
    );
    MyGroupService.getAvailables().then((response: { data: MyGroup[] }) => {
      setMyGroup(response.data);
    });

    setSidebarReload(false);
  }, [sidebarReload]);

  const handleLogout = () => {
    setMoreAnchorEl(null);
    auth.signOut();
    localStorage.removeItem(location.protocol + '//' + location.host);
    HistoryService.create({ type: History['ログアウト'] });
    navigation(URL.signin);
  };

  const drawer = (
    <Box width={drawerWidth}>
      <Toolbar sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', px: [1] }}>
        <Hidden lgUp implementation="css">
          <IconButton onClick={toggleDrawer}>
            <ChevronLeftIcon />
          </IconButton>
        </Hidden>
      </Toolbar>
      <Divider />
      <MyGroupListItems myGroup={myGroup} />
      <Divider />
      <DepartmentListItems departments={departments} />
      <Divider />
      {showAdminBoard && <AdminListItems />}
      {showAnnounceBoard && <AdminAnnounces />}
      {showHistoryManagement && <HistoryManagement />}
    </Box>
  );

  const renderMenu = (
    <Menu
      anchorEl={MoreAnchorEl}
      id="menu-mobile"
      keepMounted
      open={Boolean(MoreAnchorEl)}
      onClose={handleMenuClose}
      onClick={handleMenuClose}
      PaperProps={{
        elevation: 0,
        sx: {
          overflow: 'visible',
          filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
          mt: 1.5,
          '& .MuiAvatar-root': {
            width: 32,
            height: 32,
            ml: -0.5,
            mr: 1,
          },
          '&:before': {
            content: '""',
            display: 'block',
            position: 'absolute',
            top: 0,
            right: 14,
            width: 10,
            height: 10,
            bgcolor: 'background.paper',
            transform: 'translateY(-50%) rotate(45deg)',
            zIndex: 0,
          },
        },
      }}
      transformOrigin={{ horizontal: 'right', vertical: 'top' }}
      anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
    >
      <MenuItem onClick={personal}>
        <Avatar /> 個人設定
      </MenuItem>
      <Divider />
      {/*<MenuItem>
        <ListItemIcon>
          <FeedbackIcon fontSize="small" />
        </ListItemIcon>
        質問/要望フォーム
      </MenuItem>*/}
      <LinkPDF href='/manual/instruction.pdf' target='_blank' underline="none" color="inherit">
        <MenuItem>
          <ListItemIcon>
            <FindInPageIcon fontSize="small" />
          </ListItemIcon>
          操作説明書
        </MenuItem>
      </LinkPDF>
      <MenuItem onClick={handleLogout}>
        <ListItemIcon>
          <LogoutIcon fontSize="small" />
        </ListItemIcon>
        ログアウト
      </MenuItem>
    </Menu>
  );

  return (
    <ThemeProvider theme={mdTheme}>
      <Box sx={{ display: 'flex', flexGrow: 1 }}>
        <CssBaseline />
        <CustomAppBar position="absolute">
          <Toolbar sx={{ pr: '24px' }}>
            <Hidden lgUp implementation="css">
              <CustomIconButton edge="start" color="inherit" aria-label="open drawer" onClick={toggleDrawer}>
                <MenuIcon />
              </CustomIconButton>
            </Hidden>
            <Link color="inherit" to="/">
              <Tooltip title="ホーム">
                <IconButton aria-label="link to Home" color="inherit">
                  <HomeRoundedIcon sx={{ color: '#ffffff', fontSize: 25 }} />
                </IconButton>
              </Tooltip>
            </Link>
            {/*<Typography component="h1" variant="h6" color="inherit" noWrap sx={{ flexGrow: 1 }}>
              みてリンク
            </Typography>*/}
            <img src="/mitelink-white.svg" className="App-logo" alt="logo" width="150px" height="64px" style={{ marginLeft: "10px", cursor: "pointer" }} />
            <Typography
              component="h1"
              variant="h5"
              color="inherit"
              noWrap
              sx={{ display: { xs: 'none', sm: 'block' }, flexGrow: 1, fontStyle: '24' }}
            >
              <div style={{ marginLeft: "70px" }}>{pageName(location.pathname)}</div>
            </Typography>
            <Box sx={{ flexGrow: 1 }} />
            <WelcomeName />
            <Box sx={{ display: { xs: 'flex' } }}>
              <IconButton
                size="large"
                aria-label="show more"
                aria-controls="menu-mobile"
                aria-haspopup="true"
                onClick={handleMenuOpen}
                color="inherit"
              >
                <MoreIcon />
              </IconButton>
            </Box>
          </Toolbar>
        </CustomAppBar>
        {renderMenu}

        <Hidden lgUp implementation="css">
          <CustomDrawer variant="temporary" open={open} onClose={toggleDrawer} ModalProps={{ keepMounted: true }}>
            {drawer}
          </CustomDrawer>
        </Hidden>
        <Hidden lgDown implementation="css">
          <CustomDrawer variant="permanent">{drawer}</CustomDrawer>
        </Hidden>

        <Box
          component="main"
          sx={{
            backgroundColor: (theme) =>
              theme.palette.mode === 'light' ? theme.palette.grey[100] : theme.palette.grey[900],
            flexGrow: 1,
            height: '100dvh',
            overflow: 'auto',
            paddingLeft: { lg: '245px' },
          }}
        >
          <Toolbar />
          <Container
            maxWidth="xl"
            sx={{
              paddingTop: '1rem',
              minHeight: 'calc(100% - 64px)',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'flex-start', flexGrow: '1' }}>{children}</Box>
            <Box py={2} sx={{ alignSelf: 'center' }}>
              <Copyright />
            </Box>
          </Container>
        </Box>
      </Box>
    </ThemeProvider>
  );
};
export default GenericTemplate;
